import { createLogger } from "../../utils";
import { DateRange } from "../../types/DataRange";
import IResponseWithItems from "../../types/IResponse";
import { Base, Language } from "..";

const logger = createLogger({ name: "SalaryReport" });

class SalaryReport extends Base {
	static fromResponse(data: any): SalaryReport.Model {
		if (!data) {
			return data;
		}

		const normalizeCommissionData = (
			commission: any,
		): SalaryReport.Model.CommissionData => {
			if (!commission) {
				return {
					amount: 0,
					count: 0,
					commission: 0,
					amountSuccessful: 0,
					countSuccessful: 0,
					commissionSuccessful: 0,
				};
			}
			return {
				amount: commission.amount ?? 0,
				count: commission.count ?? 0,
				commission: commission.commission ?? 0,
				amountSuccessful: commission.amountSuccessful ?? 0,
				countSuccessful: commission.countSuccessful ?? 0,
				commissionSuccessful: commission.commissionSuccessful ?? 0,
			};
		};

		return {
			userId: data.userId ?? 0,
			callSign: data.callSign ?? "",
			lastName: data.lastName ?? "",
			firstName: data.firstName ?? "",
			middleName: data.middleName ?? "",
			ownOrders: {
				createOrderCommission: normalizeCommissionData(
					data.ownOrders?.createOrderCommission,
				),
				sentToExecutionCommission: normalizeCommissionData(
					data.ownOrders?.sentToExecutionCommission,
				),
				phoneOrderCommission: normalizeCommissionData(
					data.ownOrders?.phoneOrderCommission,
				),
				closedOrderCommission: normalizeCommissionData(
					data.ownOrders?.closedOrderCommission,
				),
			},
			sentToReassignment: {
				createOrderCommission: normalizeCommissionData(
					data.sentToReassignment?.createOrderCommission,
				),
				sentToExecutionCommission: normalizeCommissionData(
					data.sentToReassignment?.sentToExecutionCommission,
				),
				phoneOrderCommission: normalizeCommissionData(
					data.sentToReassignment?.phoneOrderCommission,
				),
				closedOrderCommission: normalizeCommissionData(
					data.sentToReassignment?.closedOrderCommission,
				),
			},
			acceptedFromReassignment: {
				sentToExecutionCommission: normalizeCommissionData(
					data.acceptedFromReassignment?.sentToExecutionCommission,
				),
				phoneOrderCommission: normalizeCommissionData(
					data.acceptedFromReassignment?.phoneOrderCommission,
				),
				closedOrderCommission: normalizeCommissionData(
					data.acceptedFromReassignment?.closedOrderCommission,
				),
			},
			total: normalizeCommissionData(data.total),
		};
	}

	public static async getDispatcherSalaryReport(
		params: SalaryReport.SearchOptions,
	): Promise<SalaryReport.GetDispatcherSalaryReportResponse | null> {
		try {
			if (!params.dateRange) {
				return null;
			}

			const response = await this.request((prpc) => {
				if (!prpc?.theirsModel?.report?.getDispatcherSalaryReport) {
					throw new Error(
						"getDispatcherSalaryReport method not found in prpc.theirsModel.report",
					);
				}
				return prpc.theirsModel.report.getDispatcherSalaryReport(
					params,
				);
			});

			logger.info(
				"[getDispatcherSalaryReport] getDispatcherSalaryReport res",
				{
					response,
				},
			);

			if (!response) {
				return null;
			}

			const result = {
				...response,
				items: response?.items?.map(this.fromResponse) || [],
			};

			return result;
		} catch (error) {
			logger.error("[getDispatcherSalaryReport] Error", error);
			return null;
		}
	}
}

declare namespace SalaryReport {
	type Model = SalaryReport.Model.Item;

	interface GetDispatcherSalaryReportResponse
		extends IResponseWithItems<Model> {}

	interface SearchOptions {
		taxiServiceIds?: number[];
		dateRange: DateRange;
		lang: Language;
	}

	namespace Model {
		interface CommissionData {
			amount: number;
			count: number;
			commission: number;
			amountSuccessful: number;
			countSuccessful: number;
			commissionSuccessful: number;
		}

		interface Item {
			userId: number;
			callSign: string;
			lastName: string;
			firstName: string;
			middleName: string;
			ownOrders: {
				createOrderCommission: CommissionData;
				sentToExecutionCommission: CommissionData;
				phoneOrderCommission: CommissionData;
				closedOrderCommission: CommissionData;
			};
			sentToReassignment: {
				createOrderCommission: CommissionData;
				sentToExecutionCommission: CommissionData;
				phoneOrderCommission: CommissionData;
				closedOrderCommission: CommissionData;
			};
			acceptedFromReassignment: {
				sentToExecutionCommission: CommissionData;
				phoneOrderCommission: CommissionData;
				closedOrderCommission: CommissionData;
			};
			total: CommissionData;
		}
	}
}

export default SalaryReport;
